import { Alert, Badge, Button, Group, Loader, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, Modal, Select } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    getGetClientEntraSignInsByClientIdQueryKey,
    useCreateClientEntraSignIn,
    useLookupClientEntraSignIn,
} from 'api/v3/client-entra-sign-ins/client-entra-sign-ins';
import {
    ClientEntraSignInLookupModel,
    ClientEntraSignInType,
    EntraDirectoryObjectKind,
    TrustedEntraTenantModel,
} from 'api/v3/models';
import { TranslationKeys } from 'i18n';
import { ErrorNotification } from 'shared/components/errorHandling';

const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const kindLabels: Record<EntraDirectoryObjectKind, TranslationKeys> = {
    Application: 'entraKindApplication',
    ManagedIdentity: 'entraKindManagedIdentity',
    Group: 'entraKindGroup',
};

type Props = {
    clientId: string;
    trustedTenants: TrustedEntraTenantModel[];
};

export const AddEntraSignInButton = ({ clientId, trustedTenants }: Props) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <Button leftSection={<Icon icon="add" />} onClick={() => setIsOpen(true)}>
                {t('addEntraSignIn')}
            </Button>
            {isOpen && (
                <AddEntraSignInModal
                    clientId={clientId}
                    trustedTenants={trustedTenants}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </>
    );
};

const onlyTenantId = (tenants: TrustedEntraTenantModel[]) => (tenants.length === 1 ? tenants[0].tenantId : null);

const AddEntraSignInModal = ({ clientId, trustedTenants, onClose: handleClose }: Props & { onClose: () => void }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    // Developer groups are only offered in tenants with developer sign-in, never in GI prod
    const developerTenants = trustedTenants.filter((tenant) => tenant.allowsDeveloperGroups);
    const [type, setType] = useState<ClientEntraSignInType>('Workload');
    const tenants = type === 'DeveloperGroup' ? developerTenants : trustedTenants;
    const [tenantId, setTenantId] = useState<string | null>(onlyTenantId(trustedTenants));
    const [entraId, setEntraId] = useState('');
    const [description, setDescription] = useState('');
    const [debouncedEntraId] = useDebouncedValue(entraId.trim(), 300);

    const isValidEntraId = guidRegex.test(debouncedEntraId);
    const lookup = useLookupClientEntraSignIn(
        clientId,
        { type, tenantId: tenantId ?? '', entraId: debouncedEntraId },
        { query: { enabled: !!tenantId && isValidEntraId, retry: false, staleTime: 60_000 } },
    );
    const { mutateAsync: createAsync, isPending, error: createError } = useCreateClientEntraSignIn();

    const isCurrent = isValidEntraId && debouncedEntraId === entraId.trim();
    // Typed a valid ID that isn't looked up yet, or the lookup is running: don't show the previous answer meanwhile
    const isLookingUp = guidRegex.test(entraId.trim()) && !!tenantId && (!isCurrent || lookup.isFetching);
    const result = isCurrent && !isLookingUp ? lookup.data?.data : undefined;
    const canSave = !!tenantId && result?.status === 'Found';

    const handleSave = async () => {
        if (!tenantId) {
            return;
        }

        await createAsync({
            clientId,
            data: { type, tenantId, entraId: debouncedEntraId, description: description || undefined },
        });
        await queryClient.invalidateQueries({ queryKey: getGetClientEntraSignInsByClientIdQueryKey(clientId) });
        handleClose();
    };

    const handleTypeChange = (value: string) => {
        const newType = value as ClientEntraSignInType;
        const allowed = newType === 'DeveloperGroup' ? developerTenants : trustedTenants;
        setType(newType);
        if (!allowed.some((tenant) => tenant.tenantId === tenantId)) {
            setTenantId(onlyTenantId(allowed));
        }
    };

    const isGroup = type === 'DeveloperGroup';

    return (
        <Modal opened={true} size={600} title={t('addEntraSignIn')} centered onClose={handleClose}>
            <Stack>
                {developerTenants.length > 0 && (
                    <SegmentedControl
                        data={[
                            { value: 'Workload', label: t('entraTypeWorkload') },
                            { value: 'DeveloperGroup', label: t('entraTypeDeveloperGroup') },
                        ]}
                        value={type}
                        onChange={handleTypeChange}
                    />
                )}
                {isGroup && (
                    <Text c="dimmed" size="sm">
                        {t('entraDeveloperGroupHint')}
                    </Text>
                )}
                <Select
                    allowDeselect={false}
                    data={tenants.map((tenant) => ({ value: tenant.tenantId, label: tenant.name }))}
                    label={t('tenant')}
                    value={tenantId}
                    required
                    onChange={setTenantId}
                />
                <TextInput
                    description={t(isGroup ? 'entraGroupIdDescription' : 'entraApplicationIdDescription')}
                    error={entraId.trim() && !guidRegex.test(entraId.trim()) ? t('invalidGuid') : undefined}
                    ff="monospace"
                    label={t(isGroup ? 'entraGroupId' : 'entraApplicationId')}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    value={entraId}
                    required
                    onChange={(event) => setEntraId(event.currentTarget.value)}
                />
                {isLookingUp ? (
                    <Group gap="xs">
                        <Loader size="xs" />
                        <Text c="dimmed" size="sm">
                            {t('entraLookupInProgress')}
                        </Text>
                    </Group>
                ) : (
                    result && <LookupResult result={result} type={type} onUseApplicationId={setEntraId} />
                )}
                {!isLookingUp && <ErrorNotification error={lookup.error} title={t('entraLookupFailed')} />}
                <TextInput
                    label={t('description')}
                    value={description}
                    onChange={(event) => setDescription(event.currentTarget.value)}
                />
                <ErrorNotification error={createError} title={t('addEntraSignIn')} />
                <Group justify="flex-end">
                    <Button variant="outline" onClick={handleClose}>
                        {t('cancel')}
                    </Button>
                    <Button disabled={!canSave} loading={isPending} onClick={handleSave}>
                        {t('add')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

type LookupResultProps = {
    result: ClientEntraSignInLookupModel;
    type: ClientEntraSignInType;
    onUseApplicationId: (applicationId: string) => void;
};

const LookupResult = ({ result, type, onUseApplicationId: handleUseApplicationId }: LookupResultProps) => {
    const { t } = useTranslation();
    const suggestedEntraId = result.suggestedEntraId;

    switch (result.status) {
        case 'Found':
            return (
                <Group gap="xs">
                    <Icon color="green" icon="check_circle" />
                    <Text fw={600}>{result.displayName}</Text>
                    {result.kind && <Badge variant="light">{t(kindLabels[result.kind])}</Badge>}
                </Group>
            );
        case 'ObjectIdInsteadOfApplicationId':
            return (
                <Alert color="orange" icon={<Icon icon="warning" />}>
                    <Stack align="flex-start" gap="xs">
                        <Text size="sm">{t('entraLookupObjectId', { name: result.displayName })}</Text>
                        {suggestedEntraId && (
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={() => handleUseApplicationId(suggestedEntraId)}
                            >
                                {t('useApplicationId', { applicationId: suggestedEntraId })}
                            </Button>
                        )}
                    </Stack>
                </Alert>
            );
        default:
            return (
                <Alert color="red" icon={<Icon icon="error" />}>
                    <Text size="sm">
                        {t(type === 'DeveloperGroup' ? 'entraLookupGroupNotFound' : 'entraLookupNotFound')}
                    </Text>
                </Alert>
            );
    }
};
